import { type ComponentProps, useContext } from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { tv } from "tailwind-variants";

import { icons } from "@/ui";

const inputOtpVariants = tv({
  slots: {
    root: "disabled:cursor-not-allowed",
    container: "flex items-center gap-2 has-disabled:opacity-50",
    group: "flex items-center",
    slot: "relative flex size-11 items-center justify-center border-y border-r border-line-strong bg-surface text-base font-medium text-content shadow-xs transition-all outline-none first:rounded-l-field first:border-l last:rounded-r-field aria-invalid:border-danger data-[active=true]:z-10 data-[active=true]:border-focus data-[active=true]:ring-2 data-[active=true]:ring-focus/30 data-[active=true]:aria-invalid:border-danger data-[active=true]:aria-invalid:ring-danger/25",
    caretWrapper: "pointer-events-none absolute inset-0 flex items-center justify-center",
    caret: "h-8 w-px animate-caret-blink bg-content duration-1000",
  },
});

const { caret, caretWrapper, container, group, root, slot } = inputOtpVariants();

const Root = ({
  className,
  containerClassName,
  ...props
}: ComponentProps<typeof OTPInput> & {
  containerClassName?: string;
}) => {
  return (
    <OTPInput
      className={root({ className })}
      containerClassName={container({ className: containerClassName })}
      data-slot="input-otp"
      {...props}
    />
  );
};

const Group = ({ className, ...props }: ComponentProps<"div">) => {
  return <div className={group({ className })} data-slot="input-otp-group" {...props} />;
};

const Slot = ({ className, index, ...props }: ComponentProps<"div"> & { index: number }) => {
  const inputOTPContext = useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

  return (
    <div
      className={slot({ className })}
      data-active={isActive}
      data-slot="input-otp-slot"
      {...props}
    >
      {char}
      {hasFakeCaret ? <Caret /> : null}
    </div>
  );
};

const Caret = () => {
  return (
    <div className={caretWrapper()}>
      <div className={caret()} />
    </div>
  );
};

const Separator = (props: ComponentProps<"div">) => {
  return (
    <div data-slot="input-otp-separator" role="separator" {...props}>
      <icons.MinusIcon />
    </div>
  );
};

export const InputOtp = {
  Root,
  Group,
  Slot,
  Separator,
};
